import { Column } from "primereact/column";
import { DataTable, DataTableSortEvent } from "primereact/datatable";
import { useRef, useEffect, useState, SetStateAction } from "react";

import { useSelector, useDispatch } from "react-redux";
import {
  setNumberOfRowsPerPage,
  setGridCurrentPageInvoice,
  getInvoices,
  setSelectedItems,
  setIsInvoicesDetailsPopupOpen,
  getInvoiceDetails,
  getConsolidatedInvoiceDetails,
  setFilterDataField,
  setInvoiceDetails,
} from "src/redux/slices/invoiceSlice";
import { useTranslation } from "react-i18next";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { RoutePaths } from "src/resources/enum";
import { useNavigate } from "react-router";
import GridPaginator from "src/components/GridPaginator/GridPaginator";
import { isInvoiceNotPaid } from "src/utils/common";
import { getTextValueById, isArabic } from "src/utils/helpers";
import { SortOrderDefault } from "src/resources/constant";
const InvoicesGrid = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { invoiceData } = useSelector((state: any) => state.invoices);
  const [sortOrder, setSortOrder] = useState<any | null>(SortOrderDefault);
  const { t } = useTranslation(["common", "invoice", "registration"]);
  const { showHistoricalData } = useSelector(
    (state: any) => state.invoices.filterData
  );
  const invoiceStatusOptions = useSelector(
    (state: any) => state.lookup.invoiceStatusOptions
  );
  const selectedItems =
    useSelector((state: any) => state.invoices.selectedItems) || []; // Fallback to an empty array

  const currentPage = useSelector(
    (state: any) => state.invoices.gridCurrentPageInvoice
  );
  const { totalRowsCount, numberOfRowsPerPage } = useSelector(
    (state: any) => state.invoices
  );
  const { sortColumn } = useSelector(
    (state: any) => state.invoices.filterData
  );

  const tableRef = useRef<DataTable<(typeof invoiceData)[0]>>(null);

  const invoiceIdTemplate = (rowData: any) => {
    return (
      <div onClick={() => handleInvoiceIdClick(rowData)}>
        {rowData.invoiceNumber}
      </div>
    );
  };

  const consolidatedInvoiceNumberTemplate = (rowData: any) => {
    return (
      <div onClick={() => handleConsolidatedInvoiceIdClick(rowData)}>
        {rowData.consolidatedInvoiceNumber}
      </div>
    );
  };
  const payButtonTemplate = (rowData: any) => {
    const isSelected = selectedItems.some(
      (item: { invoiceId: any }) => item.invoiceId === rowData.invoiceId
    );
    const isAddedToConsolidatedInvoice = rowData.consolidateInvoiceId !== null;

    if (
      isInvoiceNotPaid(rowData.invoiceStatus) &&
      !isSelected &&
      !isAddedToConsolidatedInvoice
    ) {
      return (
        <Button
          label={t("payInvoice")}
          onClick={() => payInvoiceHandler(rowData.invoiceId)}
          className=" d-flex pay-invoice-button m-0 justify-content-center"
        ></Button>
      );
    } else {
      return null;
    }
  };
  const handleInvoiceIdClick = (rowData: any) => {
    dispatch(setInvoiceDetails(rowData));
    dispatch(getInvoiceDetails(rowData.invoiceId) as any);
    dispatch(setIsInvoicesDetailsPopupOpen(true));
  };
  const handleConsolidatedInvoiceIdClick = async (rowData: any) => {
    const invoiceDetails = await dispatch(
      getConsolidatedInvoiceDetails(rowData.consolidateInvoiceId) as any
    );
    if (invoiceDetails) {
      navigate(
        `${RoutePaths.ViewConsolidatedInvoiceDetails}?consolidatedInvoiceId=${rowData.consolidateInvoiceId}`
      );
    }
  };

  const onSelectionChange = (e: any) => {
    dispatch(setSelectedItems(e.value));
  };
  useEffect(() => {
    dispatch(getInvoices() as any);
  }, []);

  function payInvoiceHandler(invoiceId: string): void {
    dispatch(getInvoiceDetails(invoiceId) as any);
    dispatch(setIsInvoicesDetailsPopupOpen(true));
  }

  const checkboxTemplate = (rowData: any) => {
    let checked = false;
    if (selectedItems !== null && selectedItems !== undefined) {
      checked = selectedItems.some(
        (item: { invoiceId: any }) => item.invoiceId === rowData.invoiceId
      );
    }
    const showCheckbox =
      isInvoiceNotPaid(rowData.invoiceStatus) &&
      rowData.consolidateInvoiceId == null;
    return (
      <div className="d-flex gap-1 align-items-center">
        {showCheckbox ? (
          <span>
            <Checkbox
              checked={checked}
              onChange={(e) => {
                const newSelectedItems = e.checked
                  ? [...selectedItems, rowData]
                  : selectedItems.filter(
                    (item: { invoiceId: any }) =>
                      item.invoiceId !== rowData.invoiceId
                  );

                onSelectionChange({ value: newSelectedItems }); // Pass the new selected items
              }}
            />
          </span>
        ) : null}
      </div>
    );
  };
  const paymentStatusTemplate = (rowData: any) => {
    return getTextValueById(rowData.invoiceStatus, invoiceStatusOptions, "id");
  };

  const onSort = (e: DataTableSortEvent) => {
    let sortFieldBEName = null;
    setSortOrder(e.sortOrder);
    dispatch(
      setFilterDataField({
        field: "sortColumn",
        value: sortFieldBEName ?? e.sortField,
      })
    );
    dispatch(
      setFilterDataField({
        field: "isAscending",
        value: e.sortOrder === 1,
      })
    );
    dispatch(setGridCurrentPageInvoice(0)); //to get latest data
  };
  return (
    <div className="grid-outer-wrapper">
      <div className="grid-inner-wrapper">
        <DataTable
          ref={tableRef}
          lazy
          className="maintenance-requests-grid view-invoice-grid single-invoice-grid"
          value={invoiceData}
          emptyMessage={showHistoricalData ? t("emptyInvoicesRecords") : t("emptyUnpaidRecords")}
          selection={selectedItems}
          onSelectionChange={onSelectionChange}
          dataKey="invoiceId"
          sortField={sortColumn || undefined}
          sortOrder={sortOrder || null} // Ensure sortOrder is a number or null
          onSort={onSort}
          scrollable
        >
          <Column
            headerClassName="p-check-box-column-header-content  "
            className="xxs-col"
            body={checkboxTemplate}
            frozen
            alignFrozen={isArabic() ? "right" : "left"}
          />

          <Column
            field="invoiceNumber"
            header={t("invoiceNo")}
            body={invoiceIdTemplate}
            className="case-id-col md-col"
            sortable
            frozen
            alignFrozen={isArabic() ? "right" : "left"}
          ></Column>
          <Column
            field="invoiceStatus"
            header={t("invoiceStatus")}
            body={paymentStatusTemplate}
            sortable
            className="md-col"
          ></Column>
          <Column
            field="propertyID"
            header={t("propertyID")}
            className="lg-col"
          ></Column>
          <Column
            field="buildingName"
            header={t("buildingNameHeader")}
            className="lg-col"
          ></Column>
          <Column
            field="dueDate"
            header={t("paymentDueDate")}
            sortable
            className="md-col"
          ></Column>
          <Column
            field="totalAmount"
            header={t("amount")}
            sortable
            className="sm-col"
          ></Column>
          <Column
            field="consolidatedInvoiceNumber"
            header={t("consolidatedInvoiceNumber")}
            body={consolidatedInvoiceNumberTemplate}
            className="case-id-col lg-col"
          ></Column>
          {<Column body={payButtonTemplate} className="sm-col"></Column>}
        </DataTable>
      </div>
      <GridPaginator
        totalRowsCount={totalRowsCount}
        currentPage={currentPage}
        numberOfRowsPerPage={numberOfRowsPerPage}
        setNumberOfRowsPerPage={setNumberOfRowsPerPage}
        setGridCurrentPage={setGridCurrentPageInvoice}
        tableRef={tableRef}
      />
    </div>
  );
};

export default InvoicesGrid;
